import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Empresa, Sitio } from '../types';
import { getSitios, createSitio, updateSitio, deleteSitio } from '../services/sitios.service';
import { getEmpresas } from '../services/empresas.service';
import { getErrorMessage } from '../utils/errorHandler';
import { getCurrentUserRole } from '../utils/auth';

declare global {
  interface Window { google: any; }
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

// ---- Carga dinámica del script de Google Maps ----
let mapScriptLoaded = false;
const loadGoogleMapsScript = (callback: () => void, onError: (msg: string) => void) => {
  if (!GOOGLE_MAPS_API_KEY) {
    onError('Falta configurar REACT_APP_GOOGLE_MAPS_KEY en el .env del frontend.');
    return;
  }
  if (mapScriptLoaded || window.google?.maps) {
    callback();
    return;
  }

  const existingScript = document.querySelector<HTMLScriptElement>('script[data-google-maps="true"]');
  if (existingScript) {
    existingScript.addEventListener('load', callback);
    existingScript.addEventListener('error', () => onError('No se pudo cargar Google Maps. Revisa la API key y restricciones.'));
    return;
  }

  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
  script.async = true;
  script.dataset.googleMaps = 'true';
  script.onload = () => { mapScriptLoaded = true; callback(); };
  script.onerror = () => {
    onError('No se pudo cargar Google Maps. Revisa la API key y restricciones para localhost.');
  };
  document.head.appendChild(script);
};

// ---- Modal de formulario de sitio ----
interface ModalProps {
  sitio?: Sitio | null;
  isAdmin: boolean;
  empresas: Empresa[];
  onClose: () => void;
  onSave: () => void;
}

const SitioModal: React.FC<ModalProps> = ({ sitio, isAdmin, empresas, onClose, onSave }) => {
  const [nombre, setNombre] = useState(sitio?.nombre || '');
  const [direccion, setDireccion] = useState(sitio?.direccion || '');
  const [lat, setLat] = useState<number>(sitio?.ubicacion?.coordinates[1] || -26.8083);
  const [lng, setLng] = useState<number>(sitio?.ubicacion?.coordinates[0] || -65.2176);
  const [radio, setRadio] = useState<number>(sitio?.radioMetros ?? sitio?.radio ?? 120);
  const [empresaId, setEmpresaId] = useState(sitio?.empresaId || '');
  const [activo, setActivo] = useState(sitio?.activo ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mapError, setMapError] = useState('');
  const [mapLoading, setMapLoading] = useState(true);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const circleRef = useRef<any>(null);

  const updateMapOverlays = useCallback((newLat: number, newLng: number, newRadio: number) => {
    if (!mapInstanceRef.current) return;
    const pos = { lat: newLat, lng: newLng };
    if (markerRef.current) markerRef.current.setPosition(pos);
    if (circleRef.current) {
      circleRef.current.setCenter(pos);
      circleRef.current.setRadius(newRadio);
    }
    mapInstanceRef.current.panTo(pos);
  }, []);

  useEffect(() => {
    if (!empresaId && isAdmin && empresas.length === 1) {
      setEmpresaId(empresas[0]._id);
    }
  }, [empresaId, isAdmin, empresas]);

  useEffect(() => {
    loadGoogleMapsScript(() => {
      if (!mapRef.current || mapInstanceRef.current) return;
      const map = new window.google.maps.Map(mapRef.current, {
        center: { lat, lng },
        zoom: 16,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: 'greedy',
      });
      mapInstanceRef.current = map;
      setMapLoading(false);

      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map,
        draggable: true,
      });
      markerRef.current = marker;

      const circle = new window.google.maps.Circle({
        strokeColor: '#3498db',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#3498db',
        fillOpacity: 0.18,
        map,
        center: { lat, lng },
        radius: radio,
      });
      circleRef.current = circle;

      map.addListener('click', (e: any) => {
        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();
        setLat(newLat);
        setLng(newLng);
        updateMapOverlays(newLat, newLng, radio);
      });

      marker.addListener('dragend', (e: any) => {
        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();
        setLat(newLat);
        setLng(newLng);
        if (circleRef.current) {
          circleRef.current.setCenter({ lat: newLat, lng: newLng });
        }
      });
    }, (message) => {
      setMapError(message);
      setMapLoading(false);
    });
    // eslint-disable-next-line
  }, []);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setMapError('Tu navegador no soporta geolocalizacion.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLat = position.coords.latitude;
        const newLng = position.coords.longitude;
        setLat(newLat);
        setLng(newLng);
        updateMapOverlays(newLat, newLng, radio);
      },
      () => {
        setMapError('No se pudo obtener tu ubicacion actual.');
      },
    );
  };

  const handleRadioChange = (val: number) => {
    setRadio(val);
    if (circleRef.current) circleRef.current.setRadius(val);
  };

  const handleLatChange = (val: number) => {
    setLat(val);
    updateMapOverlays(val, lng, radio);
  };

  const handleLngChange = (val: number) => {
    setLng(val);
    updateMapOverlays(lat, val, radio);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const payload: Partial<Sitio> = {
      nombre,
      direccion,
      ubicacion: { type: 'Point', coordinates: [lng, lat] },
      radioMetros: radio,
      activo,
      empresaId: isAdmin ? (empresaId || undefined) : undefined,
    };

    if (isAdmin && !payload.empresaId) {
      setError('Seleccione una empresa para crear el sitio.');
      setSaving(false);
      return;
    }

    try {
      if (sitio?._id) {
        await updateSitio(sitio._id, payload);
      } else {
        await createSitio(payload);
      }
      onSave();
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '700px' }}>
        <div className="modal-header">
          <h3>{sitio ? 'Editar sitio' : 'Nuevo sitio'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            {isAdmin && (
              <div className="form-group">
                <label>Empresa</label>
                <select value={empresaId} onChange={e => setEmpresaId(e.target.value)} required>
                  <option value="">Seleccionar empresa...</option>
                  {empresas.map(emp => (
                    <option key={emp._id} value={emp._id}>{emp.razonSocial}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Nombre</label>
              <input value={nombre} onChange={e => setNombre(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Direccion</label>
              <input value={direccion} onChange={e => setDireccion(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Latitud</label>
              <input
                type="number"
                step="any"
                value={lat}
                onChange={e => handleLatChange(parseFloat(e.target.value))}
                style={{ color: '#3498db' }}
              />
            </div>
            <div className="form-group">
              <label>Longitud</label>
              <input
                type="number"
                step="any"
                value={lng}
                onChange={e => handleLngChange(parseFloat(e.target.value))}
                style={{ color: '#3498db' }}
              />
            </div>
            <div className="form-group">
              <label>Radio (metros)</label>
              <input
                type="number"
                value={radio}
                onChange={e => handleRadioChange(parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          <div className="map-label">Seleccion en mapa</div>
          {mapError && <div className="error-msg">{mapError}</div>}
          {mapLoading && <div className="loading" style={{ marginBottom: '8px' }}>Cargando mapa...</div>}
          <div ref={mapRef} style={{ height: '320px', borderRadius: '6px', border: '1px solid #ddd' }} />
          <p className="map-hint">Haz clic en el mapa para tomar latitud y longitud automáticamente.</p>
          <button type="button" className="btn btn-outline btn-sm" onClick={handleUseCurrentLocation}>
            Usar mi ubicacion actual
          </button>

          <div className="checkbox-group" style={{ marginTop: '12px' }}>
            <input
              type="checkbox"
              id="activo"
              checked={activo}
              onChange={e => setActivo(e.target.checked)}
            />
            <label htmlFor="activo">Sitio activo</label>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : sitio ? 'Guardar cambios' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Página principal Sitios ----
const SitiosPage: React.FC = () => {
  const role = getCurrentUserRole();
  const isAdmin = role === 'ADMIN';
  const canCreate = role === 'ADMIN' || role === 'SUPERVISOR';
  const canManage = role === 'ADMIN';

  const [sitios, setSitios] = useState<Sitio[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState<Sitio | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [sitiosResp, empresasResp] = await Promise.all([
        getSitios(),
        isAdmin ? getEmpresas() : Promise.resolve({ data: [] as Empresa[] }),
      ]);
      setSitios(sitiosResp.data);
      setEmpresas(empresasResp.data);
    } catch {}
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleEliminar = async (id: string) => {
    if (!window.confirm('¿Eliminar este sitio?')) return;
    try {
      await deleteSitio(id);
      cargar();
    } catch {}
  };

  const handleEditar = (s: Sitio) => {
    setEditando(s);
    setShowModal(true);
  };

  const handleNuevo = () => {
    if (!canCreate) return;
    setEditando(null);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditando(null);
  };

  const handleSave = () => {
    handleClose();
    cargar();
  };

  return (
    <>
      {showModal && (
        <SitioModal
          sitio={editando}
          isAdmin={isAdmin}
          empresas={empresas}
          onClose={handleClose}
          onSave={handleSave}
        />
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 className="card-title" style={{ marginBottom: 0 }}>Listado de sitios</h2>
          {canCreate && (
            <button className="btn btn-primary" onClick={handleNuevo}>+ Nuevo sitio</button>
          )}
        </div>

        {loading ? (
          <div className="loading">Cargando...</div>
        ) : sitios.length === 0 ? (
          <div className="empty-state">No hay sitios registrados aún.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Direccion</th>
                  <th>Latitud</th>
                  <th>Longitud</th>
                  <th>Radio</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sitios.map(s => (
                  <tr key={s._id}>
                    <td>{s.nombre}</td>
                    <td>{s.direccion || '-'}</td>
                    <td>{s.ubicacion?.coordinates[1]?.toFixed(6)}</td>
                    <td>{s.ubicacion?.coordinates[0]?.toFixed(6)}</td>
                    <td>{(s.radioMetros ?? s.radio ?? 0)}m</td>
                    <td>
                      <span className={`badge ${s.activo ? 'badge-success' : 'badge-secondary'}`}>
                        {s.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      {canManage ? (
                        <div className="actions-cell">
                          <button className="btn btn-outline btn-sm" onClick={() => handleEditar(s)}>Editar</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleEliminar(s._id)}>Eliminar</button>
                        </div>
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default SitiosPage;
